import React, { useState, useMemo, useEffect, useCallback, } from 'react'

import { VTKViewerDrawerProps } from './VTKViewerDrawer.types'

import './VTKViewerDrawer.scss'

import {
  Row,
  List,
  Switch,
  Button,
  Typography,
  Divider,
  Space,
  Layout, 
  Tooltip, 
  Input, 
  Tabs, 
  Collapse,
  InputNumber
} from 'antd'

import {
  CameraOutlined,
  PictureOutlined, BlockOutlined, MacCommandOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ExpandOutlined, WindowsOutlined, RedoOutlined
} from '@ant-design/icons'

import chroma from 'chroma-js'

import { InputLabel } from '../InputLabel/InputLabel'
import { PopoverHexPicker } from '../PopoverHexPicker/PopoverHexPicker'
import { VTKLayerMenu } from '../VTKLayerMenu/VTKLayerMenu'
import { Vector3 } from '@kitware/vtk.js/types'
import { vec3 } from 'gl-matrix'

const VIEWER_KEY_CUTS = [
  {
    label: 'Pan',
    key: 'pan',
    component: <Typography.Text keyboard
      style={{ color: '#262626' }}
    >shift</Typography.Text>,
  },
  {
    label: 'Lock Rotation 2D',
    key: 'lock-rotation',
    component: <Typography.Text keyboard
      style={{ color: '#262626' }}
    >ctrl</Typography.Text>,
  },
  {
    label: 'Rotate Around Mouse',
    key: 'unicam-rotate',
    component: <Typography.Text keyboard
      style={{ color: '#262626' }}
    >alt</Typography.Text>,
  },
  {
    label: 'Zoom to Fit',
    key: 'zoom-to-fit',
    component: <Typography.Text keyboard
      style={{ color: '#262626' }}
    >r</Typography.Text>,
  }
]

const SECTION_PLANES: {name: string, normal: Vector3}[] = [
  {
    name: 'X Min',
    normal: [1, 0, 0]
  },
  {
    name: 'X Max',
    normal: [-1, 0, 0]
  },
  {
    name: 'Y Min',
    normal: [0, 1, 0]
  },
  {
    name: 'Y Max',
    normal: [0, -1, 0]
  },
  {
    name: 'Z Min',
    normal: [0, 0, 1]
  },
  {
    name: 'Z Max',
    normal: [0, 0, -1]
  },
]

const getBoundsIndex = (normal: Vector3) => {
  return normal.reduce((p,c,i) => {
    if (c === 0) return p
    return c < 0 ? i * 2 + 1 : i * 2
  }, 0)
}

export const VTKViewerDrawer: React.FunctionComponent<VTKViewerDrawerProps> = ({ dispatch, viewerState, handleScreenshot }) => {
  const [open, setOpen] = useState(false)
  const [activeKey, setActiveKey] = useState('model')

  const handleTabClick = (key) => {
    setActiveKey(key)
    setOpen(true)
  }

  const orthoMode = useMemo(() => {
    if (!viewerState || !viewerState.renderer || !viewerState.renderer.camera || typeof viewerState.renderer.camera.parallelProjection === 'undefined') return true
    return viewerState.renderer.camera.parallelProjection
  }, [viewerState])

  const backgroundColor = useMemo(() => {
    if (!viewerState || !viewerState.renderer || !viewerState.renderer.background) return '#ffffff'
    const [r, g, b] = viewerState.renderer.background
    return chroma.gl(r, g, b).hex()
  }, [viewerState])

  // const axisLabel = useMemo(() => {
  //   if (!viewerState || !viewerState.legend || !viewerState.legend.axisLabel) return ''
  //   return viewerState.legend.axisLabel
  // }, [viewerState])

  const sectionBoxBounds = useMemo(() => {
    if (!viewerState || !viewerState.widgetManager || !viewerState.widgetManager.widgets) return [0, 0, 0, 0, 0, 0]
    return viewerState.widgetManager.widgets[0].widgetState.bounds
  }, [viewerState])

  const getPlaneIndex = useCallback((normal: Vector3) => {
    if (!(viewerState && viewerState.widgetManager && viewerState.widgetManager.widgets && viewerState.widgetManager.widgets.length > 0)) return -1
    const index = viewerState.widgetManager.widgets.findIndex(w => vec3.equals(w.widgetState.normal, normal))
    return index >= 0 ? index : -1
  }, [viewerState])

  // getPlaneIndex(normal) is called in component below
  const getVisibility = useCallback((index: number) => {
    if (index < 0) return false
    return (  
      viewerState.widgetManager.widgets[index].visibility
    )
  }, [viewerState])

  const handleAdjustBounds = (val: number, normal: Vector3) => {
    if (!dispatch) return

    const boundsIndex = getBoundsIndex(normal)
    const bounds = viewerState.widgetManager.widgets[0].widgetState.bounds
    bounds[boundsIndex] = val

    dispatch({ type: 'set-bounds-section-box', value: bounds })
  }

  const handleToggleSectionBox = (value) => {
    if (!dispatch) return
    dispatch({ type: 'toggle-section-box', value })
  }

  const handleResetSectionBox = (normal?: Vector3) => {
    if (!dispatch) return
    dispatch({ type: 'reset-section-box', value: normal })
  }

  const handleHideSectionPlane = (val: boolean, normal: Vector3) => {
    if (!dispatch) return
    dispatch({ type: 'hide-section-plane', value: {toggle: val, normal} })
  }

  const handleToggleGimbal = (bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'toggle-gimbal', value: bool })
  }

  const handleToggleOrtho = (bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'toggle-ortho', value: bool })
  }

  const handleChangeBg = (color: string) => {
    if (!dispatch) return
    dispatch({ type: 'background-1', value: chroma(color).gl() })
  }

  const handleResetCamera = () => {
    if (!dispatch) return
    dispatch({ type: 'reset-camera' })
  }

  const siderButtons = [
    {
      label: 'Screenshot',
      key: 'p-screenshot',
      icon: <CameraOutlined />,
      onClick: handleScreenshot,
      component:
      <Button
        icon={<CameraOutlined />}
        style={{ border: 'none', display: 'flex', alignItems: 'center' }}
        onClick={handleScreenshot}
        key='p-screenshot'
      />
    },
    {
      label: `Toggle ${orthoMode ? 'Orthographic' : 'Perspective'}`,
      key: 'p-toggle-perspective',
      icon: <WindowsOutlined />,
      onClick: () => handleToggleOrtho(!orthoMode),
      component:
      <Button
        icon={<WindowsOutlined />}
        style={{ border: 'none', display: 'flex', alignItems: 'center' }}
        onClick={() => handleToggleOrtho(!orthoMode)}
        key='p-zoom-to-fit'
      />
    },
    {
      label: 'Reset Camera',
      key: 'p-reset-camera',
      icon: <ExpandOutlined />,
      onClick: handleResetCamera,
      component:
      <Button
        icon={<ExpandOutlined />}
        style={{ border: 'none', display: 'flex', alignItems: 'center' }}
        onClick={handleResetCamera}
        key='p-zoom-to-fit'
      />
    },
  ]

  const viewerSettings = [
    {
      key: 'section-box',
      label: 'Section Box',
      component: 
        <>
          <Row justify='space-between'>
            <InputLabel label='Show / Hide All'
              span={10}
            >
              <Switch defaultChecked={false}
                onChange={handleToggleSectionBox}
              />
            </InputLabel>
            <Button onClick={() => handleResetSectionBox()}>Reset All</Button>
          </Row>
          <List
            split={false}
          >
            { SECTION_PLANES.map(({name, normal}, i: number) => {
              const index = getPlaneIndex(normal)
              const boundsIndex = getBoundsIndex(normal)
              return (
                <List.Item key={`section-plane-${name.toLowerCase().replace(' ', '-')}`}
                  style={{
                    paddingLeft: 0,
                    paddingRight: 0
                  }}
                >
                  <InputLabel label={name}
                    span={24}
                  >
                    <Row justify='space-between'
                      align='middle'
                    >
                      <Switch
                        checked={
                          getVisibility(index)
                        }
                        onChange={(val) => {
                          handleHideSectionPlane(val, normal)
                        }}
                      />
                      <InputNumber type="number" 
                        value={sectionBoxBounds[boundsIndex]}
                        onChange={(val) => {
                          handleAdjustBounds(val, normal)
                        }}
                        style={{ width: '40%' }}
                        step={0.25}
                        precision={1}
                      />
                      <Button icon={<RedoOutlined/>}
                        shape='circle'
                        onClick={() => handleResetSectionBox(normal)}
                      />
                    </Row>
                  </InputLabel>
                </List.Item>
              )})}
          </List>
        </>
    },
    {
      key: 'renderer',
      label: 'Renderer',
      component:
      <>
        <Row>
          <InputLabel label='Background Color'
            span={24}
          >
            <PopoverHexPicker color={backgroundColor}
              colorCallback={handleChangeBg}
              swatchStyle={{ width: 28, height: 28, margin: '5px 2px' }}
            />
          </InputLabel>
        </Row>
        <Row>
          <InputLabel label='Screenshot'
            span={24}
          >
            <Button icon={<CameraOutlined />}
              onClick={handleScreenshot}
            />
          </InputLabel>
        </Row>
        <Row>
          <InputLabel label={`Toggle ${orthoMode ? 'Orthographic' : 'Perspective'}`}
            span={24}
          >
            <Switch checked={orthoMode}
              onChange={handleToggleOrtho}
            />
          </InputLabel>
        </Row>
        <Row>
          <InputLabel label='Toggle Compass Rose'
            span={24}
          >
            <Switch defaultChecked={true}
              onChange={handleToggleGimbal}
            />
          </InputLabel>
        </Row>
      </>
    },
  ]

  const viewerMenu = {
    label: 'Viewer',
    key: 'viewer',
    icon: <PictureOutlined />,
    component: (
      <Collapse defaultActiveKey={'section-box'}
        accordion
      >
        {
          viewerSettings.map((item, i) => (
            <Collapse.Panel key={item.key}
              header={item.label}
            >
              {item.component}
            </Collapse.Panel>
          ))
        }
      </Collapse>
    )
  }

  const layersMenu = {
    label: 'Model',
    key: 'model',
    icon: <BlockOutlined />,
    component: (
      <VTKLayerMenu state={viewerState}
        dispatch={dispatch}
      />
    )
  }

  const keycutsMenu = {
    label: 'Shortcuts',
    key: 'shortcuts',
    icon: <MacCommandOutlined />,
    component: (
      <List size='small'>
        {
          VIEWER_KEY_CUTS.map((item, i) => (
            <List.Item style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingLeft: 0, paddingRight: 0 }}
              key={item.key + '-' + i}
            >
              <Space style={{ width: 55, margin: '5px 0px' }}>
                {item.component}
              </Space>
              <Space style={{ flex: 1, margin: '5px 0px' }}>
                <Divider type='vertical' />
                {item.label}
              </Space>
            </List.Item>
          ))
        }
      </List>
    )
  }

  const menuTabs = [layersMenu, viewerMenu, keycutsMenu]

  return (
    <Layout.Sider className={'p-hide-scroll-y'}
      trigger={null}
      collapsible
      collapsed={!open}
      width={360}
      collapsedWidth={60}
      style={{ backgroundColor: 'white', padding: 16, borderRight: '1px solid #d9d9d9' }}
    >
      {open ?
        <Tabs activeKey={activeKey}
          animated={false}
          size="small"
          type="card"
          onTabClick={handleTabClick}
          style={{ paddingBottom: 12 }}
          tabBarStyle={{ height: 44 }}
          tabBarExtraContent={
            <MenuFoldOutlined onClick={() => setOpen(false)}
              style={{ color: '#252525', fontSize: 16 }}
            />
          }
        >
          {
            menuTabs.map((item, i) => (
              <Tabs.TabPane tab={item.label}
                key={item.key}
              >
                {
                  item.component
                }
              </Tabs.TabPane>
            ))
          }
        </Tabs>
        :
        <Space 
          direction={'vertical'} 
          size={32}
          style={{ 
            width: '100%', 
            textAlign: 'center', 
            paddingTop: 8 
          }}
        >
          <MenuUnfoldOutlined 
            onClick={() => setOpen(true)} 
            style={{ color: '#252525', fontSize: 16 }} 
          />
          <Space 
            direction={'vertical'} 
            size={24}
          > 
            {
              menuTabs.map((item, i) => (
                <Tooltip 
                  title={item.label}
                  placement={'right'}
                  key={`vtkdrawer-menutab-key-${i}`}
                >
                  {
                    React.cloneElement(item.icon, { style: { fontSize: 16 }, onClick: () => handleTabClick(item.key) })
                  }
                </Tooltip>
              ))
            }
          </Space>
          <Divider 
            style={{margin:0}} 
          />
          <Space 
            direction={'vertical'} 
            size={24} 
            style={{display:'flex'}}
          >
            {
              siderButtons.map((item, i) => (
                <Tooltip title={item.label}
                  placement={'right'}
                  key={`vtkdrawer-siderbuttons-key-${i}`}
                >
                  {
                    React.cloneElement(item.icon, { style: { fontSize: 16 }, onClick: item.onClick })
                  }
                </Tooltip>
              ))
            }
          </Space>
        </Space>
      }
    </Layout.Sider>
  )
}